import React from 'react';

import { useId } from '@floating-ui/react';
import { IconCheck, IconMinus } from '@tabler/icons-react';

import { controlFocusRing } from '../controlFocusRing';

export type CheckboxToggleEvent =
  | React.MouseEvent<HTMLDivElement>
  | React.KeyboardEvent<HTMLDivElement>;

interface CheckboxProps {
  checked: boolean;
  disabled?: boolean;
  onChange: (e: CheckboxToggleEvent) => void;
  label?: string;
  // Names a checkbox that has no visible label. Without a label or this, it stays out of the tab
  // order: its caller owns the toggle (selectionColumn, BidAccordionCard).
  ariaLabel?: string;
  size?: 'default' | 'small';
  icon?: 'check' | 'minus';
  type?: 'default' | 'gray';
  // Opt-in, as DataDisplayTable's: most checkboxes sit on surfaces that stay dark in light mode.
  supportsLightTheme?: boolean;
  'data-testid'?: string;
}

export const Checkbox: React.FC<CheckboxProps> = ({
  checked,
  disabled,
  onChange,
  label,
  ariaLabel,
  size = 'default',
  type = 'default',
  icon = 'check',
  supportsLightTheme = false,
  'data-testid': dataTestId,
}) => {
  const labelId = useId();
  const light = (classes: string) => (supportsLightTheme ? classes : '');
  const sizeClasses = size === 'small' ? 'h-3 w-3 text-[12px]' : 'h-[18px] w-[18px] text-[14px]';

  const borderClasses = {
    default: `border-[2px] ${
      disabled && checked
        ? 'border-gray-800'
        : checked
          ? `border-white ${light('light:border-gray-900')}`
          : disabled
            ? 'border-gray-800'
            : 'border-gray-600'
    }`,
    gray: `border-[2px] ${
      disabled && checked
        ? 'border-gray-800'
        : checked
          ? `border-white ${light('light:border-gray-900')}`
          : disabled
            ? 'border-gray-800'
            : 'border-gray-600'
    }`,
  };

  const iconColor = disabled ? 'text-gray-600' : `text-white ${light('light:text-gray-900')}`;

  const labelClasses = `${
    size === 'small' ? 'text-[12px]' : 'text-[14px]'
  } font-heebo text-medium ml-2 ${disabled ? 'text-gray-800' : `text-white ${light('light:text-gray-900')}`}`;

  const renderIcon = () => {
    if (!checked) return null;
    return icon === 'check' ? (
      <IconCheck stroke={2} className={iconColor} />
    ) : (
      <IconMinus stroke={2} className={iconColor} />
    );
  };

  const toggle = !disabled ? onChange : undefined;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== ' ' || !toggle) return;
    event.preventDefault();
    // Held Space repeats keydown; a native checkbox toggles once.
    if (!event.repeat) toggle(event);
  };

  const named = Boolean(label || ariaLabel);

  // A labelled checkbox toggles from its whole row, at least 24px tall, so the row is the hit
  // target (WCAG 2.5.8), not the ~12px box. A label-less one keeps the handler on the box alone:
  // its consumers (ui-kit/Table/selectionColumn, ActiveTrades/BidAccordionCard) nest it inside
  // their own click handler, and a wider hit area would toggle from around it and bubble into
  // theirs. select-none so repeated toggling doesn't select the label's text.
  return (
    <div
      className={`flex cursor-pointer items-center ${label ? 'min-h-[24px] w-fit' : ''}`}
      onClick={label ? toggle : undefined}
    >
      <div
        className={`flex items-center justify-center rounded-[3px] ${sizeClasses} ${borderClasses[type]} ${
          disabled ? '' : `hover:bg-gray-800 ${light('light:hover:bg-gray-200')}`
        } ${controlFocusRing(supportsLightTheme)} transition-all duration-300`}
        onClick={label ? undefined : toggle}
        onKeyDown={handleKeyDown}
        tabIndex={!named ? undefined : disabled ? -1 : 0}
        data-testid={dataTestId}
        role="checkbox"
        aria-labelledby={label ? labelId : undefined}
        aria-label={label ? undefined : ariaLabel}
        aria-checked={checked}
        aria-disabled={disabled || undefined}
      >
        {renderIcon()}
      </div>
      {label && (
        <span id={labelId} className={`${labelClasses} select-none`}>
          {label}
        </span>
      )}
    </div>
  );
};
